import { baseApi } from './baseApi';
import type { CreateTerminalIntentRequest, TerminalPaymentIntent } from './terminalPaymentTypes';

export interface GetnetTerminal {
  serial: string;
  name: string;
}

export interface GetnetConfig {
  enabled: boolean;
  isConfigured: boolean;
  licenseConfigured: boolean;
  environment: string; // 'qa' | 'production'
  terminals: GetnetTerminal[];
  defaultTerminalSerial?: string;
}

export interface SaveGetnetConfigRequest {
  terminals?: GetnetTerminal[];
  enabled?: boolean;
  defaultTerminalSerial?: string;
}

export const getnetApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getGetnetConfig: builder.query<GetnetConfig, void>({
      query: () => '/getnet/config',
      providesTags: ['GetnetConfig'],
    }),

    saveGetnetConfig: builder.mutation<GetnetConfig, SaveGetnetConfigRequest>({
      query: (body) => ({
        url: '/getnet/config',
        method: 'PUT',
        body,
      }),
      // AppSettings porque el guardado también mueve TerminalProvider /
      // GetnetPointEnabled, que el gate del modal de pago lee como settings
      invalidatesTags: ['GetnetConfig', 'AppSettings'],
    }),

    createGetnetIntent: builder.mutation<TerminalPaymentIntent, CreateTerminalIntentRequest>({
      query: (body) => ({
        url: '/getnet/intents',
        method: 'POST',
        body,
      }),
    }),

    // Estado del cobro en curso. El modal escucha TerminalPaymentChanged por
    // SignalR y además hace polling lento como fallback (pollingInterval en el
    // consumidor) — misma regla que el resto del tiempo real.
    getGetnetIntent: builder.query<TerminalPaymentIntent, string>({
      query: (intentId) => `/getnet/intents/${intentId}`,
    }),

    // Intent Pending vigente de una cuenta (o null): el modal lo usa para
    // reanudar un cobro abandonado en vez de crear uno nuevo y chocar.
    getGetnetPendingIntent: builder.query<TerminalPaymentIntent | null, { orderId?: string; splitId?: string }>({
      query: ({ orderId, splitId }) => {
        const params = new URLSearchParams();
        if (orderId) params.set('orderId', orderId);
        if (splitId) params.set('splitId', splitId);
        return `/getnet/intents/pending?${params.toString()}`;
      },
    }),

    cancelGetnetIntent: builder.mutation<TerminalPaymentIntent, string>({
      query: (intentId) => ({
        url: `/getnet/intents/${intentId}/cancel`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetGetnetConfigQuery,
  useSaveGetnetConfigMutation,
  useCreateGetnetIntentMutation,
  useGetGetnetIntentQuery,
  useLazyGetGetnetIntentQuery,
  useGetGetnetPendingIntentQuery,
  useCancelGetnetIntentMutation,
} = getnetApi;
